import { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import HeroSection from "../../components/HeroSection";
import MovieModal from "../../components/MovieModal";
import MovieRow from "../../components/MovieRow";
import SearchBar from "../../components/SearchBar";
import SectionMessage from "../../components/SectionMessage";
import SkeletonRow from "../../components/SkeletonRow";
import { getCatalogData, getMoviesByGenre, searchMovies } from "../../services/tmdb";
import useDebounce from "../../hooks/useDebounce";
import MovieCard from "../../components/MovieCard";

const Page = styled.main`
  display: grid;
  gap: 2rem;
  padding-bottom: 4rem;
`;

const Toolbar = styled.section`
  display: grid;
  gap: 1rem;
  width: min(1280px, calc(100% - 1.5rem));
  margin: -1rem auto 0;
  padding: 1.1rem;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radii.lg};
  background: rgba(14, 14, 14, 0.66);
  backdrop-filter: blur(18px);

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: 1rem;
  }
`;

const GenreRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem;
`;

const GenreChip = styled.button`
  padding: 0.6rem 1rem;
  border-radius: ${({ theme }) => theme.radii.pill};
  cursor: pointer;
  border: 1px solid ${({ $active, theme }) => ($active ? theme.colors.red : theme.colors.border)};
  background: ${({ $active }) => ($active ? "rgba(229, 9, 20, 0.18)" : "rgba(255, 255, 255, 0.04)")};
  color: ${({ theme }) => theme.colors.white};
  font-weight: 700;
`;

const ResultsSection = styled.section`
  display: grid;
  gap: 1rem;
  width: min(1280px, calc(100% - 1.5rem));
  margin: 0 auto;
`;

const ResultsHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: end;
  justify-content: space-between;
  gap: 0.85rem;
`;

const ResultsTitle = styled.h2`
  margin: 0;
  font-size: clamp(1.35rem, 2vw, 1.9rem);
`;

const ResultsSummary = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const ResultsGrid = styled.div`
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
`;

const genreChipOrder = [
  "Action",
  "Comedy",
  "Horror",
  "Science Fiction",
  "Animation",
  "Documentary",
  "Romance",
  "Thriller",
];

const initialCatalogState = {
  loading: true,
  error: "",
  featuredMovie: null,
  sections: [],
  genres: [],
  genresById: {},
};

function Home() {
  const [catalogState, setCatalogState] = useState(initialCatalogState);
  const [selectedGenreId, setSelectedGenreId] = useState(null);
  const [selectedMovieId, setSelectedMovieId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchState, setSearchState] = useState({ loading: false, error: "", results: [] });
  const [genreState, setGenreState] = useState({ loading: false, error: "", items: [] });
  const debouncedQuery = useDebounce(searchQuery, 450);

  const loadCatalog = useCallback(async () => {
    try {
      setCatalogState(initialCatalogState);
      const catalog = await getCatalogData();
      setCatalogState({ loading: false, error: "", ...catalog });
    } catch (error) {
      setCatalogState((currentState) => ({
        ...currentState,
        loading: false,
        error: "Nao foi possivel montar a home agora. Tente novamente em instantes.",
      }));
    }
  }, []);

  useEffect(() => {
    loadCatalog();
  }, [loadCatalog]);

  useEffect(() => {
    if (!selectedGenreId || debouncedQuery.trim().length >= 2) {
      setGenreState({ loading: false, error: "", items: [] });
      return;
    }

    let active = true;

    async function loadGenreMovies() {
      try {
        setGenreState({ loading: true, error: "", items: [] });
        const items = await getMoviesByGenre(selectedGenreId, catalogState.genresById);

        if (!active) {
          return;
        }

        setGenreState({ loading: false, error: "", items });
      } catch (error) {
        if (!active) {
          return;
        }

        setGenreState({ loading: false, error: "Nao foi possivel carregar esse genero agora.", items: [] });
      }
    }

    loadGenreMovies();

    return () => {
      active = false;
    };
  }, [catalogState.genresById, debouncedQuery, selectedGenreId]);

  useEffect(() => {
    const trimmedQuery = debouncedQuery.trim();

    if (trimmedQuery.length < 2) {
      setSearchState({ loading: false, error: "", results: [] });
      return;
    }

    let active = true;

    async function loadSearchResults() {
      try {
        setSearchState({ loading: true, error: "", results: [] });
        const results = await searchMovies(trimmedQuery, catalogState.genresById);

        if (!active) {
          return;
        }

        setSearchState({ loading: false, error: "", results });
      } catch (error) {
        if (!active) {
          return;
        }

        setSearchState({ loading: false, error: "A busca ficou indisponivel agora.", results: [] });
      }
    }

    loadSearchResults();

    return () => {
      active = false;
    };
  }, [catalogState.genresById, debouncedQuery]);

  const featuredMovie = useMemo(() => catalogState.featuredMovie, [catalogState.featuredMovie]);

  const visibleGenres = useMemo(() => {
    const prioritizedGenres = genreChipOrder
      .map((genreName) => catalogState.genres.find((genre) => genre.name === genreName))
      .filter(Boolean);

    return prioritizedGenres.slice(0, 8);
  }, [catalogState.genres]);

  const activeGenre = useMemo(
    () => catalogState.genres.find((genre) => genre.id === selectedGenreId) || null,
    [catalogState.genres, selectedGenreId]
  );

  const hasSearch = debouncedQuery.trim().length >= 2;

  const handleOpenMovie = useCallback((movieId) => {
    setSelectedMovieId(movieId);
  }, []);

  const handleCloseMovie = useCallback(() => {
    setSelectedMovieId(null);
  }, []);

  const handleToggleGenre = useCallback((genreId) => {
    setSelectedGenreId((currentGenreId) => (currentGenreId === genreId ? null : genreId));
  }, []);

  return (
    <Page>
      <HeroSection
        movie={featuredMovie}
        onPrimaryAction={handleOpenMovie}
        onSecondaryAction={handleOpenMovie}
      />

      <Toolbar>
        <SearchBar value={searchQuery} onChange={setSearchQuery} onClear={() => setSearchQuery("")} />
        <GenreRow aria-label="Filtros por genero">
          {visibleGenres.map((genre) => (
            <GenreChip
              key={genre.id}
              type="button"
              $active={selectedGenreId === genre.id}
              onClick={() => handleToggleGenre(genre.id)}
              aria-pressed={selectedGenreId === genre.id}
            >
              {genre.name}
            </GenreChip>
          ))}
        </GenreRow>
      </Toolbar>

      {catalogState.loading ? (
        <>
          <SkeletonRow />
          <SkeletonRow />
          <SkeletonRow />
        </>
      ) : null}

      {!catalogState.loading && catalogState.error ? (
        <SectionMessage
          title="A home nao conseguiu carregar"
          description={catalogState.error}
          actionLabel="Tentar novamente"
          onAction={loadCatalog}
        />
      ) : null}

      {!catalogState.loading && !catalogState.error && hasSearch && searchState.loading ? <SkeletonRow /> : null}

      {!catalogState.loading && !catalogState.error && hasSearch && !searchState.loading && searchState.error ? (
        <SectionMessage
          title="Busca indisponivel"
          description={searchState.error}
          actionLabel="Limpar busca"
          onAction={() => setSearchQuery("")}
        />
      ) : null}

      {!catalogState.loading && !catalogState.error && hasSearch && !searchState.loading && !searchState.error ? (
        searchState.results.length ? (
          <ResultsSection>
            <ResultsHeader>
              <ResultsTitle>Resultados para "{debouncedQuery.trim()}"</ResultsTitle>
              <ResultsSummary>{searchState.results.length} titulos encontrados</ResultsSummary>
            </ResultsHeader>
            <ResultsGrid>
              {searchState.results.map((movie) => (
                <MovieCard key={movie.id} movie={movie} onSelect={handleOpenMovie} />
              ))}
            </ResultsGrid>
          </ResultsSection>
        ) : (
          <SectionMessage
            title="Nenhum filme encontrado"
            description="Tente outro titulo, uma palavra-chave diferente ou limpe a busca para voltar ao catalogo curado."
            actionLabel="Limpar busca"
            onAction={() => setSearchQuery("")}
          />
        )
      ) : null}

      {!catalogState.loading && !catalogState.error && !hasSearch && selectedGenreId && genreState.loading ? <SkeletonRow /> : null}

      {!catalogState.loading && !catalogState.error && !hasSearch && selectedGenreId && !genreState.loading && genreState.error ? (
        <SectionMessage
          title="Filtro indisponivel"
          description={genreState.error}
          actionLabel="Remover filtro"
          onAction={() => setSelectedGenreId(null)}
        />
      ) : null}

      {!catalogState.loading && !catalogState.error && !hasSearch && selectedGenreId && !genreState.loading && !genreState.error ? (
        <MovieRow
          title={activeGenre ? `${activeGenre.name} em alta` : "Genero em destaque"}
          description="Curadoria dinamica baseada no filtro selecionado."
          items={genreState.items}
          onSelectMovie={handleOpenMovie}
        />
      ) : null}

      {!catalogState.loading && !catalogState.error && !hasSearch
        ? catalogState.sections.map((section) => (
            <MovieRow
              key={section.id}
              title={section.title}
              description={section.description}
              items={section.items}
              onSelectMovie={handleOpenMovie}
            />
          ))
        : null}

      <MovieModal
        movieId={selectedMovieId}
        genresById={catalogState.genresById}
        onClose={handleCloseMovie}
        onSelectMovie={handleOpenMovie}
      />
    </Page>
  );
}

export default Home;
